import { useMemo } from "react";

import type { BloodPressureDataPoint } from "~/api/bloodPressure";

interface ChartDataPoint {
  x: Date | number;
  y: number;
}

type TimeRange = "1d" | "7d" | "1m" | "6m" | "1y";

interface UseBloodPressureChartDataProps {
  apiData: BloodPressureDataPoint[] | null;
  selectedRange: TimeRange;
}

interface UseBloodPressureChartDataReturn {
  filteredData: {
    date: string;
    systolic: number;
    diastolic: number;
    hour?: number;
    datetime?: string;
  }[];
  systolicChartData: ChartDataPoint[];
  diastolicChartData: ChartDataPoint[];
}

export const useBloodPressureChartData = ({
  apiData,
  selectedRange,
}: UseBloodPressureChartDataProps): UseBloodPressureChartDataReturn => {
  return useMemo(() => {
    if (!apiData || apiData.length === 0) {
      // Return empty arrays when no data
      return {
        filteredData: [],
        systolicChartData: [],
        diastolicChartData: [],
      };
    }

    let systolicData: ChartDataPoint[] = [];
    let diastolicData: ChartDataPoint[] = [];

    if (selectedRange === "1d") {
      // For hourly data, use the hour as x value
      systolicData = apiData.map((item, _index) => ({
        x: new Date(item.date).getHours(),
        y: item.systolic,
      }));
      diastolicData = apiData.map((item, _index) => ({
        x: new Date(item.date).getHours(),
        y: item.diastolic,
      }));
    } else {
      // For other ranges (week, month, 6m, year), use date as x value
      // Since we only show dates (not hours), we can use the date directly
      systolicData = apiData.map((item, _index) => {
        // Extract just the date part from the API response
        const dateString = item.date;
        const [datePart] = dateString.split("T");
        const [year, month, day] = datePart.split("-").map(Number);

        // Create date object (timezone doesn't matter for date-only display)
        const date = new Date(year, month - 1, day);

        return {
          x: date,
          y: item.systolic,
        };
      });
      diastolicData = apiData.map((item, _index) => {
        // Extract just the date part from the API response
        const dateString = item.date;
        const [datePart] = dateString.split("T");
        const [year, month, day] = datePart.split("-").map(Number);

        // Create date object (timezone doesn't matter for date-only display)
        const date = new Date(year, month - 1, day);

        return {
          x: date,
          y: item.diastolic,
        };
      });
    }

    // Transform API data to match existing interface
    const transformedData = apiData.map((item) => ({
      date: item.date,
      systolic: item.systolic,
      diastolic: item.diastolic,
      hour: selectedRange === "1d" ? new Date(item.date).getHours() : undefined,
      datetime: selectedRange === "1d" ? item.date : undefined,
    }));

    return {
      filteredData: transformedData,
      systolicChartData: systolicData,
      diastolicChartData: diastolicData,
    };
  }, [apiData, selectedRange]);
};
